/**
 * @fileoverview Input/output schemas for the prose-expander-comparison-v2 agent.
 *
 * This composite agent runs multiple prose-expander-v2 agents in parallel
 * with different models to allow comparison of expansion outputs.
 */

import { z } from 'zod';
import { CompositeBaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// HOOK INPUT SCHEMA
// =============================================================================

/**
 * Minimal input from UI/hooks - just what the user provides.
 */
export const HookInputSchema = z.object({
  chapterId: z.string().describe('The ID of the chapter being expanded'),
  userMessage: z.string().describe('User instructions for expansion'),
  styleGuide: z.string().optional().describe('Optional style guide content'),
  /** Models to compare (1-3 models) */
  models: z.array(z.string()).min(1).max(3).describe(
    'Array of model identifiers to compare (e.g., ["gpt-4o", "claude-3-5-sonnet", "gemini-1.5-pro"])'
  ),
});

export type HookInput = z.infer<typeof HookInputSchema>;

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow - includes all context needed for expansion.
 */
export const InputSchema = z.object({
  ...CompositeBaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.styleGuide.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.draftingHistory.schema.shape,
  /** Models to compare (1-3 models) */
  models: z.array(z.string()).min(1).max(3).describe(
    'Array of model identifiers to compare'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * A single comparison result from one model.
 */
export const ComparisonItemSchema = z.object({
  /** The AI model used to generate this expansion */
  model: z.string().describe('The AI model identifier used for this expansion'),
  /** The expanded chapter content */
  chapterHtmlContent: z.string().describe('The expanded chapter prose as HTML'),
  /** Commentary explaining the expansion choices */
  summaryOfChoices: z.string().describe('Commentary on the expansion approach taken'),
  /** Time taken to generate this expansion */
  durationMs: z.number().describe('Execution time in milliseconds'),
  /** Optional display label */
  displayLabel: z.string().optional().describe('Optional label for the tab (defaults to model name)'),
});

export type ComparisonItem = z.infer<typeof ComparisonItemSchema>;

/**
 * Output includes all comparison results plus metadata.
 */
export const OutputSchema = z.object({
  /** Array of comparison results, one per model */
  results: z.array(ComparisonItemSchema).describe('Comparison results from each model'),
  /** Models that failed to produce results */
  failed: z.array(z.string()).describe('Model identifiers that failed'),
  /** Total execution time */
  durationMs: z.number().describe('Total parallel execution time in milliseconds'),
});

export type Output = z.infer<typeof OutputSchema>;
